(() => {
  if (customElements.get('solstice-carousel-pagination')) return;

  // Explicit adapters avoid mistaking filters, colour options, thumbnails or
  // the configurator's own workspace for storefront carousels.
  const copy = JSON.parse(document.getElementById('SolsticeLocaleUI')?.textContent || '{}');
  const labels = copy.labels?.split('|');
  const interpolate = (text, values) => text.replace(/__(\w+)__/g, (_, key) => String(values[key] ?? ''));
  const english = document.documentElement.lang.toLowerCase().startsWith('en');
  const adapters = [
    { track: '.solstice-trustpilot__list', scope: 'solstice-trustpilot-carousel', controls: '.solstice-trustpilot__controls', shell: '.solstice-trustpilot__track', label: english ? 'reviews' : 'les avis' },
    { track: '.ear-compositions__track', scope: 'slider-component', controls: '.ear-compositions__controls', label: english ? 'ear stacks' : 'les compositions' },
    { track: '.mega-slider-track.banner-slider', scope: 'solstice-collection-cards, [class*="collection-hero__slider-wrapper-"]', controls: '.slider-btn, .slider-dots', label: english ? 'collections' : 'les collections', anchor: '.slider-container-relative, [class*="slider-container-relative-"]' },
    { track: '.collection-slider-items', scope: '.collection-slider-wrapper, .collection-slider-items-wrapper', controls: '.carousel-nav, .carousel-dots-container', label: english ? 'the selection' : 'la sélection' },
    { track: '.inspiration-track', scope: '.inspiration-section', controls: '.carousel-nav, .inspiration-dots', label: english ? 'inspiration' : 'les inspirations', anchor: '.inspiration-carousel-wrapper' },
    { track: '.custom-related-slider', scope: '.custom-related-wrapper', controls: '.custom-nav-arrow', label: english ? 'related jewellery' : 'les bijoux associés' },
    { track: '.solstice-testimonials__list', scope: 'solstice-testimonial-slider', controls: '.solstice-testimonials__controls', label: english ? 'testimonials' : 'les témoignages' },
    { track: 'slider-component > [id^="Slider-"]:not(.thumbnail-list), slideshow-component > [id^="Slider-"]', scope: 'slider-component, slideshow-component', controls: '.slider-button:not(.slideshow__autoplay), .slider-counter', label: english ? 'the carousel' : 'le carrousel' },
  ];
  if (labels?.length === adapters.length) adapters.forEach((adapter, index) => { adapter.label = labels[index]; });
  const selector = adapters.map(adapter => adapter.track).join(',');
  let sequence = 0;

  class CarouselPagination extends HTMLElement {
    connectedCallback() {
      if (!this.track || this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.setAttribute('role', 'group');
      this.setAttribute('aria-label', copy.scroll ? interpolate(copy.scroll, {label: this.adapter.label}) : (english ? `Scroll through ${this.adapter.label}` : `Faire défiler ${this.adapter.label}`));
      this.refresh = this.refresh.bind(this);
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.defaultPrevented || event.target !== this.track) return;
        this.handleKey(event);
      }, options);
      this.addEventListener('keydown', event => this.handleKey(event), options);
      this.addEventListener('click', event => {
        const button = event.target.closest('button[data-stop]');
        if (button) this.go(Number(button.dataset.stop));
      }, options);
      this.resize = new ResizeObserver(this.refresh);
      this.resize.observe(this.track);
      this.mutations = new MutationObserver(this.refresh);
      this.mutations.observe(this.track, { childList: true });
      this.track.addEventListener('load', this.refresh, { ...options, capture: true });
      if (!this.track.hasAttribute('tabindex')) this.track.tabIndex = 0;
      this.refresh();
    }

    refresh() {
      const track = this.track;
      const cards = [...track.children].filter(card => card.getBoundingClientRect().width > 0 && !card.matches('template, script, style'));
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const viewport = track.getBoundingClientRect();
      const padding = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      const stops = [0];
      if (track.clientWidth > 0 && max > 2 && cards.length > 1) {
        cards.forEach(card => {
          const rect = card.getBoundingClientRect();
          let left = rect.left - viewport.left + track.scrollLeft - padding;
          if (getComputedStyle(card).scrollSnapAlign.includes('center')) left -= (track.clientWidth - rect.width) / 2 - padding;
          const position = Math.max(0, Math.min(max, left));
          if (position > stops[stops.length - 1] + 2) stops.push(position);
        });
        if (max > stops[stops.length - 1] + 2) stops.push(max);
      }
      this.stops = stops;
      this.hidden = stops.length < 2;
      if (this.children.length !== stops.length) {
        const hadFocus = this.contains(document.activeElement);
        this.replaceChildren(...stops.map((_, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.stop = index;
          button.setAttribute('aria-label', copy.position ? interpolate(copy.position, {index: index + 1, count: stops.length, label: this.adapter.label}) : (english ? `Position ${index + 1} of ${stops.length}: ${this.adapter.label}` : `Position ${index + 1} sur ${stops.length} : ${this.adapter.label}`));
          button.setAttribute('aria-controls', track.id);
          return button;
        }));
        this.update();
        if (hadFocus && !this.hidden) this.children[this.active]?.focus({ preventScroll: true });
      } else this.update();
    }

    update() {
      if (!this.stops) return;
      const left = this.track.scrollLeft;
      this.active = this.stops.reduce((best, stop, index) => Math.abs(stop - left) < Math.abs(this.stops[best] - left) ? index : best, 0);
      // A moving window keeps long review carousels compact on mobile.
      const start = Math.max(0, Math.min(this.active - 3, this.stops.length - 7));
      [...this.children].forEach((button, index) => {
        button.hidden = index < start || index >= start + 7;
        button.setAttribute('aria-current', index === this.active ? 'true' : 'false');
        button.tabIndex = index === this.active ? 0 : -1;
        button.toggleAttribute('data-edge', (index === start && start > 0) || (index === start + 6 && index < this.stops.length - 1));
      });
    }

    go(index) {
      const target = Math.max(0, Math.min(index, this.stops.length - 1));
      this.track.scrollTo({ left: this.stops[target], behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    handleKey(event) {
      const index = event.target.matches('button[data-stop]') ? Number(event.target.dataset.stop) : this.active;
      const targets = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: this.stops.length - 1 };
      if (!(event.key in targets)) return;
      event.preventDefault();
      const target = Math.max(0, Math.min(targets[event.key], this.stops.length - 1));
      this.go(target);
      if (this.contains(event.target)) {
        this.children[target].hidden = false;
        this.children[target].focus({ preventScroll: true });
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resize?.disconnect();
      this.mutations?.disconnect();
      this.events = null;
    }
  }
  customElements.define('solstice-carousel-pagination', CarouselPagination);

  function enhance(track) {
    if (track.dataset.solsticeCarousel || track.closest('#ear-configurator-app, .announcement-bar, .thumbnail-slider')) return;
    const adapter = adapters.find(item => track.matches(item.track));
    const scope = track.closest(adapter.scope);
    if (!scope) return;
    if (!track.id) track.id = `SolsticeCarousel-${++sequence}`;
    const pagination = document.createElement('solstice-carousel-pagination');
    pagination.track = track;
    pagination.adapter = adapter;
    const anchor = (adapter.anchor && track.closest(adapter.anchor)) || track;
    anchor.insertAdjacentElement('afterend', pagination);
    scope.querySelectorAll(adapter.controls).forEach(control => control.setAttribute('data-solstice-carousel-legacy', ''));
    if (adapter.shell) track.closest(adapter.shell)?.setAttribute('data-solstice-carousel-shell', '');
    track.dataset.solsticeCarousel = 'true';
  }

  function scan(root) {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root instanceof Element && root.closest('#ear-configurator-placeholder, #ear-configurator-app')) return;
    if (root.matches?.(selector)) enhance(root);
    root.querySelectorAll(selector).forEach(enhance);
  }
  function start() {
    scan(document);
    new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(scan));
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', event => scan(event.target));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href], [data-open-ear-composer]');
    if (!link) return;
    let opensComposer = link.hasAttribute('data-open-ear-composer');
    if (!opensComposer && link.href) {
      const url = new URL(link.href, location.origin);
      opensComposer = url.origin === location.origin && /\/pages\/compositeur-bijoux-d-oreille\/?$/.test(url.pathname);
    }
    if (!opensComposer) return;
    event.preventDefault();
    window.dispatchEvent(new CustomEvent('vue-toggle-app', { detail: true }));
  });
})();

// Keep the floating composer away from visible purchase controls on small screens.
(() => {
  const mobile = matchMedia('(max-width: 749px)');
  let scheduled = false;
  const update = () => {
    scheduled = false;
    const visible = mobile.matches && [...document.querySelectorAll('main product-form button[name="add"], .sticky-atc button')].some(button => {
      const r = button.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight && getComputedStyle(button).visibility !== 'hidden';
    });
    document.body.classList.toggle('solstice-purchase-visible', visible);
  };
  const schedule = () => { if (!scheduled) { scheduled = true; requestAnimationFrame(update); } };
  window.addEventListener('scroll', schedule, {passive:true});
  window.addEventListener('resize', schedule);
  document.addEventListener('shopify:section:load', schedule);
  update();
})();
